import { useStationStore } from '@/state/useStationStore';
import { useConfigurationStore } from '@/state/useConfigurationStore';
import { useUIStore } from '@/state/useUIStore';
import { useVehicleStore } from '@/state/useVehicleStore';
import { useInterval } from '@/hooks/useInterval';
import { useEffect } from 'react';
import { Navbar } from '@/ui/components/Navbar';
import { StationTabs } from '@/ui/components/StationTabs';
import { TransactionRecoveryDialog } from '@/ui/components/TransactionRecoveryDialog';
import { ChargePointTab } from '@/ui/tabs/ChargePointTab';
import { ChargingProfilesTab } from '@/ui/tabs/ChargingProfilesTab';
import { EventLogTab } from '@/ui/tabs/EventLogTab';
import { OfflineSessionTab } from '@/ui/tabs/OfflineSessionTab';
import { ReplayTab } from '@/ui/tabs/ReplayTab';
import { SettingsTab } from '@/ui/tabs/SettingsTab';
import { SnapshotsTab } from '@/ui/tabs/SnapshotsTab';
import { DisplayMessagesTab } from '@/ui/tabs/DisplayMessagesTab';
import { HistoryTab } from '@/ui/tabs/HistoryTab';
import { ToastViewport } from '@/ui/components/Toast';
import { isV2x } from '@/lib/ocppProfile';

/** FEAT-011 — 1 Hz SoC tick. Cheap no-op when no DC session is active. */
const SOC_TICK_MS = 1000;

/**
 * BUG-017 / BUG-018 — MeterValues interval is the runtime value of the
 * `MeterValueSampleInterval` configuration key (default 60 s). It is read
 * reactively from `useConfigurationStore` so:
 *   - a Settings-tab edit re-renders the page → useInterval recreates the
 *     timer with the new delay;
 *   - an inbound ChangeConfiguration from the CSMS goes through the same
 *     store and also re-renders.
 *
 * BUG-019 — Heartbeat interval works the same way: read from the
 * `HeartbeatInterval` config key (default 300 s = 5 min). The action
 * itself early-returns when no adapter is wired, so the timer can tick
 * while disconnected without side effects.
 *
 * Clamp range protects the runtime: minimum 1 s (no busy-loop), maximum
 * 24 h (no effectively-disabled interval — set the value high and the
 * timer naturally won't fire during a normal session). Falls back to the
 * documented default when the value is missing, non-integer, or out of range.
 */
const MV_INTERVAL_SEC_DEFAULT = 60;
const HEARTBEAT_INTERVAL_SEC_DEFAULT = 300;
const TICK_SEC_MIN = 1;
const TICK_SEC_MAX = 86_400;

function parseIntervalSec(raw: string | undefined, fallback: number): number {
  if (raw === undefined) return fallback;
  const n = Number(raw);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return fallback;
  if (n < TICK_SEC_MIN) return TICK_SEC_MIN;
  if (n > TICK_SEC_MAX) return TICK_SEC_MAX;
  return n;
}

export function StationPage(): JSX.Element {
  const activeTab = useUIStore((s) => s.activeTab);
  const stationId = useStationStore((s) => s.stationId);
  const applySocTick = useStationStore((s) => s.applySocTick);
  const emitMeterValues = useStationStore((s) => s.emitMeterValuesForActiveTx);
  const heartbeat = useStationStore((s) => s.heartbeat);
  const refreshConfig = useConfigurationStore((s) => s.refresh);
  const mvIntervalSec = useConfigurationStore((s) =>
    parseIntervalSec(
      s.entries.find((e) => e.key === 'MeterValueSampleInterval')?.value,
      MV_INTERVAL_SEC_DEFAULT,
    ),
  );
  // Session 10d — heartbeat key differs across OCPP versions. v1.6 uses
  // the flat "HeartbeatInterval"; v2.0.1's Variables model places it
  // under the OCPPCommCtrlr component. The store flattens both into a
  // single configurationKeys table — see protocol/v201/variables.ts.
  const ocppVersion = useStationStore((s) => s.ocppVersion);
  const heartbeatKey = isV2x(ocppVersion) ? 'OCPPCommCtrlr.HeartbeatInterval' : 'HeartbeatInterval';
  const heartbeatIntervalSec = useConfigurationStore((s) =>
    parseIntervalSec(
      s.entries.find((e) => e.key === heartbeatKey)?.value,
      HEARTBEAT_INTERVAL_SEC_DEFAULT,
    ),
  );
  const loadVehicles = useVehicleStore((s) => s.loadAll);

  // Vehicle library is workspace-wide; load once when the station page mounts
  // so the connector-binding dropdown has rows to choose from.
  useEffect(() => {
    void loadVehicles();
  }, [loadVehicles]);

  // BUG-018 — hydrate the configuration store on mount so the very first
  // MeterValues / Heartbeat tick reads the persisted intervals (rather
  // than defaults) even if the user never opened the Settings tab.
  // Session 18 — also pass the live `evses` so the seeded
  // `NumberOfConnectors` row picks up the real count instead of the
  // hardcoded "3" sentinel.
  const evses = useStationStore((s) => s.evses);
  useEffect(() => {
    void refreshConfig(stationId, ocppVersion, evses);
  }, [refreshConfig, stationId, ocppVersion, evses]);

  useInterval(() => {
    applySocTick(SOC_TICK_MS / 1000);
  }, SOC_TICK_MS);

  useInterval(() => {
    emitMeterValues();
  }, mvIntervalSec * 1000);

  // BUG-019 — periodic Heartbeat at the configured interval. The action
  // already no-ops when no adapter is wired, so this can tick safely
  // while disconnected.
  useInterval(() => {
    void heartbeat();
  }, heartbeatIntervalSec * 1000);

  return (
    <div className="flex min-h-full flex-col bg-slate-950 text-slate-200">
      <Navbar />
      <StationTabs />
      <main className="mx-auto w-full max-w-5xl">
        {activeTab === 'chargepoint' ? <ChargePointTab /> : null}
        {activeTab === 'eventLog' ? <EventLogTab /> : null}
        {activeTab === 'profiles' ? <ChargingProfilesTab /> : null}
        {activeTab === 'offline' ? <OfflineSessionTab /> : null}
        {activeTab === 'replay' ? <ReplayTab /> : null}
        {activeTab === 'display' ? <DisplayMessagesTab /> : null}
        {activeTab === 'history' ? <HistoryTab /> : null}
        {activeTab === 'snapshots' ? <SnapshotsTab /> : null}
        {activeTab === 'settings' ? <SettingsTab /> : null}
      </main>
      <TransactionRecoveryDialog />
      <ToastViewport />
    </div>
  );
}
